import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';

function EventAdmin() {
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('events').select('*').order('date', { ascending: true });
    if (!error) setEvents(data);
    setLoading(false);
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleFileChange = e => setImageFile(e.target.files[0]);

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    let image_url = '';
    if (imageFile) {
      const fileName = `${Date.now()}-${imageFile.name}`;
      const { error: uploadError } = await supabase.storage.from('event-images').upload(fileName, imageFile);
      if (uploadError) {
        setMessage('Failed to upload image.');
        setSubmitting(false);
        return;
      }
      image_url = supabase.storage.from('event-images').getPublicUrl(fileName).data.publicUrl;
    }
    let result;
    if (editId) {
      // Update event
      const updateData = { ...form };
      if (image_url) updateData.image_url = image_url;
      result = await supabase.from('events').update(updateData).eq('id', editId);
    } else {
      // Add new event
      result = await supabase.from('events').insert([{ ...form, image_url }]);
    }
    if (result.error) {
      setMessage('Failed to save event.');
    } else {
      setMessage(editId ? 'Event updated successfully!' : 'Event added successfully!');
      setForm({ title: '', description: '', date: '', location: '' });
      setImageFile(null);
      setShowForm(false);
      setEditId(null);
      fetchEvents();
    }
    setSubmitting(false);
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date || '',
      location: event.location || '',
    });
    setImageFile(null);
    setEditId(event.id);
    setShowForm(true);
    setMessage('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    const { error } = await supabase.from('events').delete().eq('id', id);
    if (!error) {
      setMessage('Event deleted');
      fetchEvents();
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        {/* Sidebar */}
        <div className="col-md-3 col-lg-2 mb-4">
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-body p-0">
              <ul className="nav flex-column nav-pills p-2 gap-2">
                <li className="nav-item">
                  <Link className="nav-link w-100 text-start" to="/admin">View Submissions</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link w-100 text-start" to="/admin?tab=publications">Publications</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link w-100 text-start" to="/admin?tab=editorial">Editorial Board Manager</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link w-100 text-start" to="/admin?tab=blog">Blog Management</Link>
                </li>
                <li className="nav-item">
                  <a className="nav-link w-100 text-start active" href="/admin/events">Event Management</a>
                </li>
                {/* Add more admin links here */}
              </ul>
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="col-md-9 col-lg-10">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="fw-bold">Events</h2>
            <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ title: '', description: '', date: '', location: '' }); setImageFile(null); setMessage(''); }}>
              {showForm ? (editId ? 'Cancel Edit' : 'Close') : 'Add Event'}
            </button>
          </div>
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : events.length === 0 ? (
            <div className="alert alert-info text-center">No events found.</div>
          ) : (
            <div className="row g-4 mb-5">
              {events.map(event => (
                <div className="col-md-6 col-lg-4" key={event.id}>
                  <div className="card h-100 shadow-sm border-0 rounded-4">
                    {event.image_url && (
                      <img src={event.image_url} alt={event.title} className="card-img-top rounded-top-4" style={{maxHeight: 220, objectFit: 'cover'}} />
                    )}
                    <div className="card-body d-flex flex-column justify-content-between">
                      <div>
                        <h5 className="card-title fw-bold">{event.title}</h5>
                        <div className="mb-2 text-secondary">{event.date} | {event.location}</div>
                        <p className="card-text">{event.description}</p>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <button className="btn btn-sm btn-info" onClick={() => handleEdit(event)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(event.id)}>Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {showForm && (
            <div className="card shadow-sm border-0 p-4 mb-4 rounded-4">
              <h4 className="mb-3">{editId ? 'Edit Event' : 'Add New Event'}</h4>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input type="text" className="form-control" name="title" value={form.title} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" value={form.description} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input type="date" className="form-control" name="date" value={form.date} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Location</label>
                  <input type="text" className="form-control" name="location" value={form.location} onChange={handleChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Image</label>
                  <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} />
                </div>
                {submitting && <div className="alert alert-info">Submitting...</div>}
                {message && <div className="alert alert-info">{message}</div>}
                <button type="submit" className="btn btn-primary" disabled={submitting}>{editId ? 'Update Event' : 'Add Event'}</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventAdmin; 